// Chart interaction reducer — the pure state machine behind the History view's
// zoom window + on-chart selection. Owns three fields that used to live as
// scattered React state on HistoryPage, glued together by effects:
//
//   visible  — the zoomed sub-window [fromMs, toMs] (null ⇒ follow the data)
//   band     — a drag-selected time window + the row that drew it
//   clicked  — a single pinned point
//
// The tracking lifecycle (relative-vs-absolute, pause-on-gesture) stays in
// rangeTracking.ts; the store composes the two. Keeping this reducer pure and
// separate is what makes the band/click interplay testable without uplot or a
// live store — the source of the stacked "stuck band" race.
//
// Selection rules (the bug-prone part, pinned here as one source of truth):
//   - a band supersedes a click (setBand clears clicked)
//   - a real click supersedes a band (setClicked with a ts clears band)
//   - a null click is the END of a band drag, NOT a clear — it leaves the band
//     intact (uplot fires a trailing mouseup with no ts; wiping the band there
//     is the old "band vanishes the instant you finish drawing it" bug)

export interface BandSelection {
  fromMs:           number;
  toMs:             number;
  /** The machine row whose chart drew this band. Drives scroll-into-view and
   *  lets the source chart ignore its own band echo (the stuck-band fix). */
  sourceMachineKey: string;
}

export interface ChartInteractionState {
  visible: [number, number] | null;
  band:    BandSelection | null;
  clicked: number | null;
}

export type ChartInteractionEvent =
  // A settled zoom/pan gesture, a range re-pin, or a reset — set the visible
  // window. Selection is preserved (a band is a time window, still valid).
  | { type: 'setVisible'; visible: [number, number] | null }
  // A drag-selected band completed. Clears any pinned click.
  | { type: 'setBand'; band: BandSelection }
  // A point click (ts) pins it and clears the band; a null ts is a band-drag
  // mouseup and only clears a prior pinned click, never the band.
  | { type: 'setClicked'; ts: number | null }
  // Explicit "clear selection" — drops both band and click.
  | { type: 'clearSelection' };

export const initialChartInteraction: ChartInteractionState = {
  visible: null,
  band:    null,
  clicked: null,
};

function sameWindow(a: [number, number] | null, b: [number, number] | null): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return a[0] === b[0] && a[1] === b[1];
}

export function chartInteractionReducer(
  state: ChartInteractionState,
  event: ChartInteractionEvent,
): ChartInteractionState {
  switch (event.type) {
    case 'setVisible':
      if (sameWindow(state.visible, event.visible)) return state;
      return { ...state, visible: event.visible };

    case 'setBand':
      return { ...state, band: event.band, clicked: null };

    case 'setClicked':
      if (event.ts == null) {
        // End-of-band mouseup — leave the band, only drop a stale click.
        if (state.clicked == null) return state;
        return { ...state, clicked: null };
      }
      return { ...state, clicked: event.ts, band: null };

    case 'clearSelection':
      if (state.band == null && state.clicked == null) return state;
      return { ...state, band: null, clicked: null };
  }
}
